import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { Slide, Fade, Zoom } from "react-awesome-reveal";
import {
  TbCalendarEvent,
  TbChartBar,
  TbBulb,
  TbMap2,
  TbRocket,
  TbCheck,
} from "react-icons/tb";
import "../../styles/VSLSection.css";

/* ── EmailJS config (desde .env) ── */
const EJ_SERVICE   = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EJ_AUTOREPLY = process.env.REACT_APP_EMAILJS_TEMPLATE_AUTOREPLY;
const EJ_INTERNO   = process.env.REACT_APP_EMAILJS_TEMPLATE_INTERNO;
const EJ_KEY       = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const YOUTUBE_URL = "https://www.youtube.com/embed/gGhPb8lribQ";

const bullets = [
  { icon: <TbChartBar size={20} />, text: "Nivel de digitalización actual" },
  { icon: <TbBulb     size={20} />, text: "Oportunidades de mejora identificadas" },
  { icon: <TbMap2     size={20} />, text: "Arquitectura de solución recomendada" },
  { icon: <TbRocket   size={20} />, text: "Roadmap estimado por fases" },
];

const facturacionOpciones = [
  "Menos de USD 500k / año",
  "USD 500k – 2M / año",
  "USD 2M – 10M / año",
  "Más de USD 10M / año",
  "Prefiero no decirlo",
];

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const VSLSection = () => {
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    telefono: "",
    empresa: "",
    cargo: "",
    empleados: "",
    facturacion: "",
    comentario: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const templateParams = {
      nombre:      form.nombre,
      email:       form.email,
      telefono:    form.telefono,
      empresa:     form.empresa,
      cargo:       form.cargo,
      empleados:   form.empleados,
      facturacion: form.facturacion,
      comentario:  form.comentario || "—",
    };

    try {
      /* 1. Email interno al equipo */
      await emailjs.send(EJ_SERVICE, EJ_INTERNO, templateParams, EJ_KEY);

      /* 2. Auto-reply al lead */
      await emailjs.send(EJ_SERVICE, EJ_AUTOREPLY, templateParams, EJ_KEY);

      setSubmitted(true);
    } catch (err) {
      console.error("[EmailJS] Error:", err);
      setError("Error al enviar el formulario. Intentá más tarde.");
    } finally {
      setLoading(false);
    }
  };

  const OPTIONAL = ["comentario"];
  const allFilled = Object.entries(form)
    .filter(([k]) => !OPTIONAL.includes(k))
    .every(([, v]) => v.trim() !== "");

  return (
    <section className="vsl-section" id="contacto">
      <div className="container py-5">

        {/* ── Header ── */}
        <Slide direction="up" duration={700} triggerOnce>
          <div className="text-center mb-5">
            <span className="eco-badge">CONTACTO</span>
            <h2 className="eco-main-title text-white mt-3">
              Mirá cómo transformamos operaciones reales
            </h2>
            <p className="eco-main-sub text-white-50">
              Problema → Costo oculto → Nueva forma de operar → Cómo lo implementamos → Invitación al diagnóstico.
            </p>
          </div>
        </Slide>

        {/* ── Video ── */}
        <Zoom duration={700} triggerOnce>
          <div className="vsl-video-wrap">
            <div className="vsl-video-frame">
              <iframe
                src={YOUTUBE_URL}
                title="Video VSL Twinexa"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </Zoom>

        {/* ── Two-column: bullets + form ── */}
        <div className="vsl-bottom">

          {/* Left – call description */}
          <Fade direction="left" delay={200} duration={700} triggerOnce>
            <div className="vsl-left">
              <div className="vsl-left-inner">
                <TbCalendarEvent size={36} color="#a259f7" />
                <h3 className="vsl-left-title">
                  Diagnóstico Estratégico gratuito
                </h3>
                <p className="vsl-left-sub text-white-50">
                  Una llamada de 30 minutos donde analizamos tu operación y
                  te mostramos exactamente qué podemos hacer.
                </p>
                <ul className="vsl-bullets">
                  {bullets.map((b, i) => (
                    <li key={i}>
                      <span className="vsl-bullet-icon">{b.icon}</span>
                      <span>{b.text}</span>
                    </li>
                  ))}
                </ul>
                <p className="vsl-disclaimer text-white-50">
                  Sin compromiso. Sin discurso de ventas.
                  Solo claridad sobre tu próximo paso.
                </p>
              </div>
            </div>
          </Fade>

          {/* Right – form */}
          <Fade direction="right" delay={300} duration={700} triggerOnce>
            <div className="vsl-right">
              {submitted ? (
                <div className="vsl-success">
                  <TbCheck size={48} color="#a259f7" />
                  <h4 className="text-white mt-3">¡Solicitud enviada!</h4>
                  <p className="text-white-50">
                    Te vamos a contactar a la brevedad para confirmar el horario.
                  </p>
                </div>
              ) : (
                <form className="vsl-form" onSubmit={handleSubmit} noValidate>
                  <h4 className="vsl-form-title">Reservá tu diagnóstico</h4>

                  <div className="vsl-field">
                    <label htmlFor="nombre">Nombre completo</label>
                    <input
                      id="nombre" name="nombre" type="text"
                      placeholder="Juan García"
                      value={form.nombre} onChange={handleChange} required
                    />
                  </div>

                  <div className="vsl-field">
                    <label htmlFor="email">Email</label>
                    <input
                      id="email" name="email" type="email"
                      placeholder="juan@empresa.com"
                      value={form.email} onChange={handleChange} required
                    />
                  </div>

                  <div className="vsl-field">
                    <label htmlFor="telefono">Teléfono / WhatsApp</label>
                    <input
                      id="telefono" name="telefono" type="tel"
                      placeholder="+54 9 351 000-0000"
                      value={form.telefono} onChange={handleChange} required
                    />
                  </div>

                  <div className="vsl-field">
                    <label htmlFor="empresa">Empresa</label>
                    <input
                      id="empresa" name="empresa" type="text"
                      placeholder="Nombre de tu empresa"
                      value={form.empresa} onChange={handleChange} required
                    />
                  </div>

                  <div className="vsl-field">
                    <label htmlFor="cargo">Cargo</label>
                    <input
                      id="cargo" name="cargo" type="text"
                      placeholder="Gerente de Operaciones, CEO..."
                      value={form.cargo} onChange={handleChange} required
                    />
                  </div>

                  <div className="vsl-field">
                    <label htmlFor="empleados">Cantidad de empleados</label>
                    <select
                      id="empleados" name="empleados"
                      value={form.empleados} onChange={handleChange} required
                    >
                      <option value="" disabled>Seleccioná una opción</option>
                      <option>1 – 10</option>
                      <option>11 – 50</option>
                      <option>51 – 200</option>
                      <option>201 – 500</option>
                      <option>Más de 500</option>
                    </select>
                  </div>

                  <div className="vsl-field">
                    <label htmlFor="facturacion">
                      Facturación estimada anual
                      <span className="vsl-field-hint"> — filtra si es el momento correcto</span>
                    </label>
                    <select
                      id="facturacion" name="facturacion"
                      value={form.facturacion} onChange={handleChange} required
                    >
                      <option value="" disabled>Seleccioná una opción</option>
                      {facturacionOpciones.map((o) => (
                        <option key={o}>{o}</option>
                      ))}
                    </select>
                  </div>

                  <div className="vsl-field">
                    <label htmlFor="comentario">
                      Comentario adicional
                      <span className="vsl-field-hint"> — opcional</span>
                    </label>
                    <textarea
                      id="comentario" name="comentario" rows={3}
                      placeholder="Contanos brevemente tu situación actual o qué problema querés resolver..."
                      value={form.comentario} onChange={handleChange}
                    />
                  </div>

                  <button
                    type="submit"
                    className="vsl-submit btn btn-connect"
                    disabled={!allFilled || loading}
                  >
                    {loading ? "Enviando…" : "Reservar Diagnóstico →"}
                  </button>

                  {error && <p className="vsl-error">⚠ {error}</p>}
                </form>
              )}
            </div>
          </Fade>

        </div>
      </div>
    </section>
  );
};

export default VSLSection;
